// @flow

import * as React from 'react';
import WindowResize from '@ta/events.window-resize';

type Props = {|
  /** Additional styling for the component */
  className: string,
  /** Children to auto-resize */
  children: React.Node,
  /** The default font size if no auto resizing is needed */
  defaultFontSizePx: number,
  /** The minimum font size to shrink to */
  minFontSizePx: number,
  /** Automatically resize on window resize */
  resizeOnScreenResize?: boolean,
|};

type State = {|
  resizedFontSize: number,
  numIters: number,
|};

const MAX_ITERATIONS = 4;
// font size multipler to (hopefully) make text fit if it doesn't after the maximum number of iterations
const END_JUMP_MULTIPLIER = 0.9;

/**
 * Attempts to resize font size of a given text body to fit the width specified in the css for className. Adapted from
 * https://grok.dev.tripadvisor.com/xref/mainline/site/js3/src/ta/util/Element.js#380
 * Assumes all units are in px
 */
export default class AutoResizer extends React.Component<Props, State> {
  static defaultProps = {
    minFontSizePx: 8,
    defaultFontSizePx: 14,
    resizeOnScreenResize: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = { resizedFontSize: props.defaultFontSizePx, numIters: 0 };
  }

  componentDidMount() {
    this.makeFit();
  }

  makeFit() {
    if (!this.containerRef || this.state.numIters >= MAX_ITERATIONS) {
      return;
    }
    const {
      scrollWidth = 0,
      clientWidth = 0,
      style: { paddingLeft, paddingRight, fontSize },
    } = this.containerRef;

    const fontSizeNum = parseFloat(fontSize);
    if (clientWidth >= scrollWidth || clientWidth <= 0 || fontSizeNum <= this.props.minFontSizePx) {
      return;
    }

    const paddingWidth = (parseFloat(paddingLeft) || 0) + (parseFloat(paddingRight) || 0);
    let multiplier = (clientWidth - paddingWidth) / (scrollWidth - paddingWidth);
    if (this.state.numIters === MAX_ITERATIONS - 1) {
      multiplier = END_JUMP_MULTIPLIER;
    }

    const newFontSize = Math.max(fontSizeNum * multiplier, this.props.minFontSizePx);

    this.setState(
      {
        resizedFontSize: newFontSize,
        numIters: this.state.numIters + 1,
      },
      this.makeFit,
    );
  }

  resizeCallback = () => {
    this.setState(
      {
        resizedFontSize: this.props.defaultFontSizePx,
        numIters: 0,
      },
      this.makeFit,
    );
  };

  containerRef = {};

  render() {
    return (
      <div
        style={{ fontSize: this.state.resizedFontSize }}
        ref={input => {
          this.containerRef = input;
        }}
        className={this.props.className}
      >
        {this.props.children}
        {this.props.resizeOnScreenResize ? (
          <WindowResize callback={this.resizeCallback} useDebounce />
        ) : null}
      </div>
    );
  }
}
